import { action, set } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task, type TaskInstance } from 'ember-concurrency';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type HotelSettingsAutopilotController from 'lumen/controllers/hotel/settings/autopilot';
import type { PaAutopilotScheduleOption } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

interface AutopilotModel {
  pricingAssistantAutopilotScheduleOptionsTask: TaskInstance<
    PaAutopilotScheduleOption[] | null
  >;
  saveAndUpdateAutopilotSettings: Task<{ isSuccess: boolean }, [unknown]>;
}

type TransitionWithIsAborted = Transition & { isAborted: boolean };

export default class HotelSettingsAutopilotRoute extends Route {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  leaveWithoutSaving = false;

  confirmLeaveTask = task(
    { drop: true },
    async (transition: TransitionWithIsAborted) => {
      this.leaveWithoutSaving = await this.alerting.showWarningAlert({
        title: this.intl.t('pricing_assistant.warning_banner.header'),
        dangerMode: true,
        buttons: {
          cancel: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.secondary_button.label',
            ),
          },
          confirm: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.primary_button.label',
            ),
          },
        },
      });

      if (this.leaveWithoutSaving) {
        transition.retry();
      }
    },
  );
  pricingAssistantAutopilotScheduleOptionsTask = task(
    { restartable: true },
    async () => {
      let apiResponse = null;

      try {
        apiResponse = await this.store.query<PaAutopilotScheduleOption>(
          'pa-autopilot-schedule-option',
          {
            namespace: APIGATEWAY,
            typeKey: 'pricing-assistant/autopilot/schedule/options',
            subscription_id: this.hotelAndMarketService.hotel.id,
          },
        );

        return [...apiResponse];
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null; // in this case something went wrong with the request
    },
  );

  saveAndUpdateAutopilotSettingsTask = task(
    { drop: true },
    async (autopilotSettings) => {
      let apiResponse = null;

      try {
        const standardParams = {
          subscription_id: this.hotelAndMarketService.hotel.id!,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/autopilot/',
          standardParams,
          this.ajax.host,
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        apiResponse = await this.ajax.put(requestUrl, {
          data: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            pa_autopilot_settings: autopilotSettings,
          },
          contentType: 'application/json',
        });

        set(
          this.hotelAndMarketService.hotel,
          'paAutopilotSettings',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          apiResponse.pa_autopilot_settings,
        );

        return {
          isSuccess: true,
        };
      } catch (error) {
        this.basicErrorHandling(error);

        return {
          isSuccess: false,
        };
      }
    },
  );

  activate(transition: Transition): void {
    super.activate(transition);

    // Reset the "leave without saving" state on route activation so the willTransition hook will recheck the unsaved changes
    this.leaveWithoutSaving = false;
  }

  basicErrorHandling(error: unknown): void {
    if (this.access.isStaffUser && console) {
      console.error(error);
    }

    if (error) {
      this.errorTracking.captureException(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      text: getAPIErrorMessage(error, this.intl),
    });
  }

  beforeModel(transition: Transition): void {
    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    if (!this.access.hasPricingAssistantSettingsAutopilotTab) {
      this.router.transitionTo('hotel.settings.index');
    }
  }

  model(): AutopilotModel {
    const model: AutopilotModel = {
      saveAndUpdateAutopilotSettings: this.saveAndUpdateAutopilotSettingsTask,
      pricingAssistantAutopilotScheduleOptionsTask:
        this.pricingAssistantAutopilotScheduleOptionsTask.perform(),
    };

    return model;
  }

  resetController(
    controller: HotelSettingsAutopilotController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // Reset controller variables
      controller.hasUserChangedAutopilotSettingsValue = false;
    }
  }

  @action
  willTransition(transition: TransitionWithIsAborted): void {
    /* eslint-disable ember/no-controller-access-in-routes */
    const hasChanges = (
      this.controllerFor(
        'hotel.settings.autopilot',
      ) as HotelSettingsAutopilotController
    ).hasUserChangedAutopilotSettingsValue;
    /* eslint-enable ember/no-controller-access-in-routes */

    if (!hasChanges || transition.isAborted || this.leaveWithoutSaving) {
      return;
    }

    transition.abort();
    void this.confirmLeaveTask.perform(transition);
  }
}
